import type { HTMLAttributes, ReactNode } from 'react';
import { useState } from 'react';

import { Button } from '../components/button';
import { cn } from '../lib/cn';

export type TimelineItemStatus = 'complete' | 'current' | 'future' | 'gap' | 'no-data' | 'degraded';

export interface TimelineItem {
  id: string;
  label: string;
  timestamp?: string;
  source?: string;
  detail?: ReactNode;
  status: TimelineItemStatus;
}

export interface TimelineProps extends Omit<HTMLAttributes<HTMLOListElement>, 'aria-label' | 'children' | 'role'> {
  ariaLabel: string;
  items: TimelineItem[];
  showAllLabel?: string;
  showLessLabel?: string;
  role?: string;
}

const dotClasses: Record<TimelineItemStatus, string> = {
  complete: 'bg-[var(--success-light)] text-[var(--foreground)]',
  current: 'bg-[var(--secondary-light)] text-[var(--foreground)]',
  future: 'border border-[var(--border)] bg-[var(--background)] text-[var(--foreground-muted)]',
  gap: 'border border-dashed border-[var(--border)] bg-[var(--background-muted)] text-[var(--foreground-muted)]',
  'no-data': 'border border-dashed border-[var(--border)] bg-[var(--background-muted)] text-[var(--foreground-muted)]',
  degraded: 'bg-[var(--info-light)] text-[var(--info-text)]',
};

function TimelineIcon({ status }: { status: TimelineItemStatus }) {
  const glyph = status === 'complete' ? '✓' : status === 'current' ? '●' : status === 'future' ? '○' : '—';
  return <span aria-hidden="true" className="text-xs leading-none">{glyph}</span>;
}

export function Timeline({ ariaLabel, className, items, showAllLabel, showLessLabel, ...props }: TimelineProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const isCollapsible = items.length > 15 && Boolean(showAllLabel && showLessLabel);
  const visibleItems = isCollapsible && !isExpanded ? items.slice(-5) : items;

  return (
    <div className={className}>
      <ol {...props} aria-label={ariaLabel} role="list">
        {visibleItems.map((item, index) => (
          <li className="relative flex gap-3 pb-5 last:pb-0" data-timeline-state={item.status} key={item.id}>
            {index < visibleItems.length - 1 ? <span aria-hidden="true" className={cn('absolute left-[5px] top-3 h-[calc(100%-0.25rem)] border-l border-[var(--border)]', item.status === 'gap' && 'border-dashed')} /> : null}
            <span className={cn('z-10 mt-0.5 flex size-3 shrink-0 items-center justify-center rounded-full', dotClasses[item.status])}><TimelineIcon status={item.status} /></span>
            <div className="min-w-0">
              <p className="text-[13px] font-medium text-[var(--foreground)]">{item.label}</p>
              {item.timestamp ? <p className="text-xs text-[var(--foreground-muted)]">{item.timestamp}</p> : null}
              {item.source ? <p className="text-xs text-[var(--foreground-muted)]">{item.source}</p> : null}
              {item.detail ? <div className="text-xs text-[var(--foreground-muted)]">{item.detail}</div> : null}
            </div>
          </li>
        ))}
      </ol>
      {isCollapsible && showAllLabel && showLessLabel ? <Button aria-expanded={isExpanded} className="mt-3" onClick={() => setIsExpanded((value) => !value)} size="sm" variant="ghost">{isExpanded ? showLessLabel : showAllLabel}</Button> : null}
    </div>
  );
}
